import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, ChevronUp, ChevronDown, Eye, EyeOff, Trash2, Settings, Video, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { SectionRenderer } from '@/components/sections/SectionRenderer';
import { SECTION_TYPES, getDefaultContent } from '@/components/sections/section-types';
import { SectionEditor } from '@/components/admin/SectionEditor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface Section {
  id: string;
  section_type: string;
  content: Record<string, unknown>;
  layout_variant: string;
  is_visible: boolean;
  sort_order: number;
}

interface CustomPageRendererProps {
  sections: Section[];
  onChange: (sections: Section[]) => void;
  pageId?: string;
}

export function CustomPageRenderer({ sections, onChange, pageId }: CustomPageRendererProps) {
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const [showAddSection, setShowAddSection] = useState(false);
  const [editingVideoSection, setEditingVideoSection] = useState<number | null>(null);
  const [editingSection, setEditingSection] = useState<number | null>(null);

  const addSection = async (sectionType: string) => {
    if (!assertCanWrite()) return;
    if (!pageId) return;
    
    const newSection = {
      organization_id: orgId,
      page_id: pageId,
      page_slug: null,
      section_type: sectionType,
      content: getDefaultContent(sectionType),
      layout_variant: 'default',
      is_visible: true,
      sort_order: sections.length,
    };

    const { data, error } = await supabase
      .from('page_sections')
      .insert([newSection])
      .select()
      .single();

    if (error) {
      toast.error('Failed to add section');
    } else if (data) {
      onChange([...sections, {
        ...data,
        content: (data.content || {}) as Record<string, unknown>,
        layout_variant: data.layout_variant || 'default',
      }]);
      toast.success('Section added');
      setShowAddSection(false);
    }
  };

  const updateSection = (index: number, updates: Partial<Section>) => {
    const newSections = [...sections];
    newSections[index] = { ...newSections[index], ...updates };
    onChange(newSections);
  };

  const deleteSection = async (index: number) => {
    if (!assertCanWrite()) return;
    const section = sections[index];
    const { error } = await supabase
      .from('page_sections')
      .delete()
      .eq('id', section.id);

    if (error) {
      toast.error('Failed to delete section');
    } else {
      onChange(sections.filter((_, i) => i !== index));
      toast.success('Section deleted');
    }
  };

  const moveSection = async (index: number, direction: 'up' | 'down') => {
    if (!assertCanWrite()) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;

    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    
    // Update sort orders
    newSections.forEach((s, i) => {
      s.sort_order = i;
    });
    
    onChange(newSections);

    // Persist to database
    for (const section of newSections) {
      await supabase
        .from('page_sections')
        .update({ sort_order: section.sort_order })
        .eq('id', section.id);
    }
  };

  const toggleVisibility = (index: number) => {
    if (!assertCanWrite()) return;
    updateSection(index, { is_visible: !sections[index].is_visible });
  };

  const updateVideoUrl = (index: number, url: string) => {
    const section = sections[index];
    updateSection(index, {
      content: { ...section.content, videoUrl: url }
    });
  };

  if (sections.length === 0) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center bg-muted/30 p-8">
        <div className="text-center max-w-md">
          <h3 className="text-lg font-semibold text-foreground mb-2">This page is empty</h3>
          <p className="text-muted-foreground mb-6">Add sections to build your page content</p>
          <Button disabled={!canWrite} onClick={() => { if (assertCanWrite()) setShowAddSection(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Section
          </Button>
        </div>

        <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Section</DialogTitle>
              <DialogDescription>Choose a section type to add to your page</DialogDescription>
            </DialogHeader>
            <div className="grid gap-2 pt-4 max-h-[60vh] overflow-y-auto">
              {SECTION_TYPES.map((type) => (
                <Button
                  key={type.id}
                  variant="outline"
                  className="justify-start h-auto py-3 px-4"
                  onClick={() => addSection(type.id)}
                  disabled={!canWrite}
                >
                  <div className="text-left">
                    <div className="font-medium">{type.name}</div>
                    <div className="text-xs text-muted-foreground">{type.description}</div>
                  </div>
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {sections.map((section, index) => (
        <div 
          key={section.id} 
          className={cn(
            "relative group",
            !section.is_visible && "opacity-50"
          )}
        >
          {/* Section controls - lower z-index to not clash with header */}
          <div className="absolute top-16 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 bg-background/95 backdrop-blur rounded-lg p-1 shadow-lg border border-border">
            <span className="text-xs font-medium text-muted-foreground px-2">
              {SECTION_TYPES.find(t => t.id === section.section_type)?.name || section.section_type}
            </span>
            <div className="h-4 w-px bg-border" />
            
            {/* Video URL editor */}
            {section.section_type === 'video' && (
              <Popover open={editingVideoSection === index} onOpenChange={(open) => setEditingVideoSection(open ? index : null)}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <Video className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="font-medium">Video URL</Label>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => setEditingVideoSection(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <Input
                      placeholder="https://youtube.com/watch?v=..."
                      value={(section.content.videoUrl as string) || ''}
                      onChange={(e) => updateVideoUrl(index, e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Supports YouTube, Vimeo, or direct video URLs (.mp4, .webm)
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setEditingSection(index)}
              disabled={!canWrite}
              aria-label="Section settings"
              title="Section settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveSection(index, 'up')} disabled={!canWrite || index === 0}>
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveSection(index, 'down')} disabled={!canWrite || index === sections.length - 1}>
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button disabled={!canWrite} variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleVisibility(index)}>
              {section.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button disabled={!canWrite} variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => deleteSection(index)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Section content */}
          <SectionRenderer section={section} preview />
        </div>
      ))}

      {/* Add section button */}
      <div className="p-8 flex justify-center border-t border-dashed border-border bg-muted/30">
        <Button disabled={!canWrite} variant="outline" onClick={() => { if (assertCanWrite()) setShowAddSection(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Section
        </Button>
      </div>

      {/* Section settings (saved with the page's Save button) */}
      <Dialog open={editingSection !== null} onOpenChange={(open) => { if (!open) setEditingSection(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSection !== null
                ? SECTION_TYPES.find((t) => t.id === sections[editingSection]?.section_type)?.name ?? 'Section'
                : 'Section'}{' '}
              settings
            </DialogTitle>
            <DialogDescription>Changes appear in the preview straight away. Save the page to publish them.</DialogDescription>
          </DialogHeader>
          {editingSection !== null && sections[editingSection] && (
            <SectionEditor
              key={sections[editingSection].id}
              section={sections[editingSection]}
              onSave={(next) => {
                updateSection(editingSection, { content: next.content, layout_variant: next.layout_variant });
                setEditingSection(null);
              }}
              onCancel={() => setEditingSection(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Section</DialogTitle>
            <DialogDescription>Choose a section type to add to your page</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 pt-4 max-h-[60vh] overflow-y-auto">
            {SECTION_TYPES.map((type) => (
              <Button
                key={type.id}
                variant="outline"
                className="justify-start h-auto py-3 px-4"
                onClick={() => addSection(type.id)}
              >
                <div className="text-left">
                  <div className="font-medium">{type.name}</div>
                  <div className="text-xs text-muted-foreground">{type.description}</div>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
